<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useBilling } from '../composables/useBilling'
import { useCreditMessagesReady } from '../composables/useCreditMessagesReady'
import { resolveComparisonPlans } from '../service/comparePlans'
import { useCreditStore } from '../stores/creditStore'

import {
  FREE_PLAN_DURATION_DAYS,
  getAccountDetailPackageId,
  getFreeAccessSnapshot,
  isFreePlanPackage,
  isFreeTrialPackage,
  useAccount,
  useWorkspace,
} from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui/icon'

withDefaults(defineProps<{ expanded?: boolean }>(), { expanded: false })

const TRIAL_DURATION_DAYS = 14
const TRIAL_COUNTDOWN_DAYS = 6
const TRIAL_ENDING_DAYS = 1

const account = useAccount()
const workspace = useWorkspace()
const store = useCreditStore()
const billing = useBilling()
const router = useRouter()
const format = useFormat()
const { t } = useI18n()
const messagesReady = useCreditMessagesReady('credits.sidebarPlan.freeTrialTitle')

const packageId = computed(() => getAccountDetailPackageId(account.accountDetail.value))
const isTrial = computed(() => isFreeTrialPackage(packageId.value))
const isFree = computed(() => isFreePlanPackage(packageId.value))
const visible = computed(
  () =>
    messagesReady.value &&
    (isTrial.value || isFree.value) &&
    account.accountDetail.value?.only_dashboard_permission !== true,
)
const freeSnapshot = computed(() =>
  getFreeAccessSnapshot(account.accountDetail.value, account.account.value),
)
const trialBalanceReady = computed(() => store.loaded && store.trialDaysRemaining !== null)
const trialDaysLeft = computed(() => store.trialDaysRemaining ?? 0)
const trialIsEnding = computed(
  () => trialBalanceReady.value && trialDaysLeft.value <= TRIAL_ENDING_DAYS,
)
const trialShowCountdown = computed(
  () => trialBalanceReady.value && trialDaysLeft.value <= TRIAL_COUNTDOWN_DAYS,
)
const isEnded = computed(() => !isTrial.value && freeSnapshot.value.status === 'ended')
const isEnding = computed(() =>
  isTrial.value ? trialIsEnding.value : freeSnapshot.value.status === 'ending',
)
const showCountdown = computed(() =>
  isTrial.value
    ? trialShowCountdown.value
    : freeSnapshot.value.status === 'countdown' || freeSnapshot.value.status === 'ending',
)
const progress = computed(() => {
  if (isTrial.value) {
    return Math.min(
      100,
      Math.max(6, ((TRIAL_DURATION_DAYS - trialDaysLeft.value) / TRIAL_DURATION_DAYS) * 100),
    )
  }
  const elapsed = freeSnapshot.value.elapsedDays ?? 0
  return Math.min(100, Math.max(6, ((elapsed - 1) / FREE_PLAN_DURATION_DAYS) * 100))
})
const countdownLabel = computed(() => {
  const days = isTrial.value ? trialDaysLeft.value : (freeSnapshot.value.daysLeft ?? 0)
  if (days <= 0) return t('credits.sidebarPlan.endsToday')
  if (days === 1) return t('credits.sidebarPlan.endsTomorrow')
  return t('credits.sidebarPlan.daysLeft', { count: days })
})
const endedDateLabel = computed(() => {
  const endedAt = freeSnapshot.value.endedAt
  return endedAt ? format.formatDate(endedAt) : ''
})
const quotedPlan = computed(() => {
  const plans = resolveComparisonPlans(billing.packages.value, { quote: billing.quote.value })
  return plans.find((plan) => plan.key === 'pro') ?? null
})
const workspaceLabel = computed(
  () => workspace.workspace.value?.label || t('credits.sidebarPlan.workspaceFallback'),
)
const actionLabel = computed(() => {
  if (isEnded.value) return t('credits.sidebarPlan.turnBackOn')
  if (isTrial.value) {
    return isEnding.value
      ? t('credits.sidebarPlan.pickPlan')
      : t('credits.sidebarPlan.comparePlans')
  }
  return isEnding.value
    ? t('credits.sidebarPlan.keepWorkspaceRunning', { workspace: workspaceLabel.value })
    : t('credits.sidebarPlan.comparePlans')
})

function openPlans(): void {
  void router.push({ name: 'ComparePlans' })
}

async function load(force = false): Promise<void> {
  await Promise.all([store.fetchBalance(force), billing.loadPlans(force)])
}

onMounted(() => void load())
watch(account.accountId, (next, previous) => {
  if (next === previous) return
  store.reset()
  void load(true)
})
</script>

<template>
  <div
    v-if="visible"
    class="sidebar-plan-card pointer-events-none relative h-[4.5rem] w-full flex-none"
    :data-plan="isTrial ? 'free-trial' : 'free-plan'"
  >
    <button
      type="button"
      class="absolute bottom-md start-1/2 grid size-10 -translate-x-1/2 place-items-center rounded-full border bg-surface shadow-sm transition-[opacity,transform] duration-100 motion-reduce:transition-none"
      :class="[
        isEnding || isEnded ? 'border-warning/30 text-warning' : 'border-primary/20 text-primary',
        expanded
          ? 'pointer-events-none scale-95 opacity-0 delay-200'
          : 'pointer-events-auto scale-100 opacity-100 delay-150',
      ]"
      :aria-label="isTrial ? t('credits.sidebarPlan.freeTrial') : t('credits.sidebarPlan.freePlan')"
      :aria-hidden="expanded"
      :tabindex="expanded ? -1 : 0"
      :data-plan="isTrial ? 'free-trial' : 'free-plan'"
      :data-testid="expanded ? undefined : 'sidebar-plan-card'"
      @click="openPlans"
    >
      <Icon v-if="isEnded" name="sparkles" :size="14" aria-hidden="true" />
      <span v-else class="size-3 rounded-full border border-current" aria-hidden="true" />
    </button>

    <div
      class="sidebar-plan-card--expanded absolute inset-x-0 bottom-md rounded-2xl border bg-surface p-md shadow-sm transition-[opacity,transform,background-color,border-color] duration-150 motion-reduce:transition-none"
      :class="[
        isEnded
          ? 'border-border bg-surface-sunken'
          : isEnding
            ? 'border-warning/30 bg-warning-soft'
            : 'border-border',
        expanded
          ? 'pointer-events-auto translate-y-0 opacity-100 delay-300'
          : 'pointer-events-none translate-y-1 opacity-0 delay-0',
      ]"
      :aria-hidden="!expanded"
      :data-plan="isTrial ? 'free-trial' : 'free-plan'"
      :data-testid="expanded ? 'sidebar-plan-card' : undefined"
    >
      <div class="flex items-center gap-xs text-[0.9375rem] font-semibold text-fg">
        <span
          v-if="!showCountdown"
          class="grid size-5 place-items-center"
          :class="isEnded ? 'text-muted' : isTrial ? 'text-success' : 'text-primary'"
          aria-hidden="true"
        >
          <Icon v-if="isEnded" name="sparkles" :size="14" />
          <span v-else class="size-2.5 rounded-full bg-current" />
        </span>
        <span v-if="isEnded">{{ t('credits.sidebarPlan.freeEndedTitle') }}</span>
        <span v-else-if="isTrial">{{ t('credits.sidebarPlan.freeTrialTitle') }}</span>
        <span v-else>{{ t('credits.sidebarPlan.freePlanTitle') }}</span>
      </div>

      <p v-if="isEnded" class="mt-sm text-sm leading-relaxed text-muted">
        {{ t('credits.sidebarPlan.frozenOn', { date: endedDateLabel }) }}
        {{ t('credits.sidebarPlan.readExportWorks') }}
      </p>
      <template v-else-if="showCountdown">
        <div
          class="mt-sm flex items-center gap-xs text-sm"
          :class="isEnding ? 'text-warning' : 'text-muted'"
        >
          <Icon name="clock" :size="12" aria-hidden="true" />
          <span>{{ countdownLabel }}</span>
        </div>
        <div class="mt-sm h-1.5 overflow-hidden rounded-full bg-surface-sunken">
          <div
            class="h-full rounded-full transition-[width]"
            :class="isEnding ? 'bg-warning' : 'bg-primary'"
            :style="{ width: `${progress}%` }"
            data-testid="sidebar-plan-progress"
          />
        </div>
      </template>
      <p v-else-if="isFree" class="mt-xs text-xs text-muted">
        {{ t('credits.sidebarPlan.freeActive') }}
      </p>

      <div v-if="isTrial" class="mt-md border-t border-border pt-md">
        <p class="m-0 text-sm text-subtle">{{ t('credits.sidebarPlan.planAfterTrial') }}</p>
        <p v-if="quotedPlan" class="m-0 mt-2xs text-[0.9375rem] font-semibold text-fg">
          {{ quotedPlan.name }}
          <span v-if="quotedPlan.price !== null" class="text-lg text-primary">
            {{
              format.formatCurrency(quotedPlan.price, {
                currency: quotedPlan.currencyCode,
                minFrac: 0,
                maxFrac: 0,
              })
            }}
          </span>
          <span v-if="quotedPlan.price !== null" class="font-normal text-subtle">
            {{ t('credits.sidebarPlan.perMonth') }}
          </span>
        </p>
        <p v-if="quotedPlan" class="m-0 mt-2xs text-[0.6875rem] text-subtle">
          {{ t('credits.sidebarPlan.basedOnSetup') }}
        </p>
        <p v-else class="m-0 mt-xs flex items-center gap-xs text-xs font-medium text-muted">
          <Icon name="repeat" :size="12" aria-hidden="true" />
          <span>{{ t('credits.sidebarPlan.calculating') }}</span>
        </p>
      </div>

      <button
        type="button"
        class="mt-md w-full rounded-xl px-sm py-xs text-sm font-semibold transition-colors"
        :class="
          isEnded || isEnding
            ? 'bg-emphasis-deep text-emphasis-fg hover:bg-emphasis-ink'
            : 'border-2 border-border text-fg hover:bg-surface-sunken'
        "
        :tabindex="expanded ? 0 : -1"
        data-testid="sidebar-plan-action"
        @click="openPlans"
      >
        {{ actionLabel }}
      </button>
    </div>
  </div>
</template>
